import { createFileRoute, notFound } from "@tanstack/react-router";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { FaqSourceNote } from "@/components/FaqSourceNote";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildFaqSchema, buildSpeakableSchema } from "@/lib/schema";
import {
  NOTARY_COUNTRY_LANDINGS,
  getNotaryCountryLanding,
  type NotaryCountryLanding,
} from "@/content/notary-corpus/countries";
import { NOTARY_DOC_LANDINGS } from "@/content/notary-corpus/documents";

function faqsEn(c: NotaryCountryLanding) {
  return [
    { q: `How are Thai documents legalised for use in ${c.nameEn}?`, a: c.chainEn },
    {
      q: `Can ${c.nameEn} accept a Thai Apostille yet?`,
      a: c.hagueParty
        ? `${c.nameEn} is already a party to the 1961 Apostille Convention. Thailand deposited its instrument of accession on 30 June 2026 and the Convention enters into force for Thailand on 28 February 2027, after an eight-month objection period. Until then, Thai documents still require MFA legalisation followed by embassy attestation (sources: hcch.net and the Thai Department of Consular Affairs).`
        : `No. ${c.nameEn} is not a party to the 1961 Apostille Convention, so even after the Convention enters into force for Thailand on 28 February 2027, documents for ${c.nameEn} will still need MFA legalisation and attestation at the ${c.nameEn} embassy in Thailand.`,
    },
    { q: `What changes once the Convention is in force for Thailand?`, a: c.afterEn },
    { q: `What language must documents be translated into for ${c.nameEn}?`, a: c.languageEn },
    { q: `Which documents are most often sent to ${c.nameEn}?`, a: c.topDocsEn.join(" · ") },
    { q: `Why do documents get rejected in ${c.nameEn}?`, a: c.pitfallsEn.join(" · ") },
    {
      q: "Do I have to sign in person?",
      a: "Yes. You must sign in front of the notarial services attorney and present your original passport or Thai ID card. Documents signed beforehand must be re-signed; for certified true copies the original must be shown.",
    },
    {
      q: "How are fees calculated?",
      a: "Attorney fees depend on the number of documents and seals. MFA legalisation fees and embassy fees are charged at the official rates of those authorities, separately from our service fee. A written quote is provided before work starts.",
    },
    {
      q: "Do you guarantee acceptance abroad?",
      a: "No. Acceptance is at the discretion of the receiving authority. We check the format, notarial wording, name spelling against your passport, document age and legalisation sequence beforehand, and re-do the work at no charge if the error is ours.",
    },
  ];
}

export const Route = createFileRoute("/en/notary/country/$country")({
  loader: ({ params }) => {
    const c = getNotaryCountryLanding(params.country);
    if (!c) throw notFound();
    return { c };
  },
  component: NotaryCountryPageEN,
  head: ({ params, loaderData }) => {
    if (!loaderData) {
      return { meta: [{ title: "Country not found" }, { name: "robots", content: "noindex" }] };
    }
    const site = getActiveSiteUrl();
    const { c } = loaderData;
    const title = `Notarise Thai Documents for ${c.nameEn} 2026`;
    const description = `Legalising Thai documents for ${c.nameEn}: attorney notarisation, translation, MFA legalisation and embassy attestation — plus language rules, common rejection causes and the 28 Feb 2027 Apostille change.`;
    return {
      ...pageHeadForProvince({
        title,
        description,
        path: `/en/notary/country/${params.country}`,
        type: "article",
        locale: "en_US",
        alternates: [{ lang: "th", path: `/notary/country/${params.country}` }],
        keywords: [
          `notary thailand for ${c.nameEn.toLowerCase()}`,
          `legalise thai documents ${c.nameEn.toLowerCase()}`,
          `power of attorney thailand ${c.nameEn.toLowerCase()}`,
          `thai apostille ${c.nameEn.toLowerCase()}`,
          `mfa legalisation ${c.nameEn.toLowerCase()}`,
        ],
      }),
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "Home", url: `${site}/en` },
              { name: "Notary Public", url: `${site}/en/notary` },
              { name: `Documents for ${c.nameEn}`, url: `${site}/en/notary/country/${params.country}` },
            ]),
          ),
        },
        { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqsEn(c))) },
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema(`/en/notary/country/${params.country}`)) },
      ],
    };
  },
});

function NotaryCountryPageEN() {
  const { c } = Route.useLoaderData();
  const faqs = faqsEn(c);
  return (
    <>
      <SiteHeader />
      <main className="container mx-auto max-w-4xl px-4 py-10">
        <nav aria-label="breadcrumb" className="mb-4 text-sm text-muted-foreground">
          <a href="/en" className="hover:underline">Home</a> <span>/</span>{" "}
          <a href="/en/notary" className="hover:underline">Notary Public</a> <span>/</span>{" "}
          <span>{c.nameEn}</span>
        </nav>

        <h1 className="mb-3 text-3xl font-bold md:text-4xl">
          Notarising Thai Documents for {c.nameEn} — Attorney, MFA and Embassy
        </h1>
        <p className="mb-6 text-lg text-muted-foreground" data-speakable="true">{c.chainEn}</p>

        <div className="mb-8 flex flex-wrap gap-2 text-sm">
          <a href="/contact" className="rounded-md bg-primary px-4 py-2 font-medium text-primary-foreground">Get a free quote →</a>
          <a href="/en/notary/faq" className="rounded-md border px-4 py-2 font-medium hover:bg-muted">Notary FAQ</a>
          <a href={`/notary/country/${c.slug}`} className="rounded-md border px-4 py-2 text-muted-foreground hover:bg-muted">ภาษาไทย →</a>
        </div>

        <section className="mb-10 rounded-lg border bg-muted/20 p-6">
          <h2 className="mb-3 text-xl font-bold">Apostille Convention status — {c.nameEn}</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">
            {c.hagueParty
              ? `${c.nameEn} is already a party to the 1961 Hague Apostille Convention.`
              : `${c.nameEn} is not a party to the 1961 Hague Apostille Convention.`}{" "}
            Thailand deposited its instrument of accession on 30 June 2026, and the Convention enters into force for
            Thailand on 28 February 2027 following an eight-month objection period. Until that date, Thai documents
            continue to follow the MFA legalisation route.
          </p>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{c.afterEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Documents most often sent to {c.nameEn}</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.topDocsEn.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10 rounded-lg border bg-card p-6">
          <h2 className="mb-3 text-xl font-bold">Language and translation requirements</h2>
          <p className="text-sm leading-relaxed text-muted-foreground">{c.languageEn}</p>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Common pitfalls with {c.nameEn}</h2>
          <ul className="list-disc space-y-1 pl-6 text-sm text-muted-foreground">
            {c.pitfallsEn.map((d: string, i: number) => <li key={i}>{d}</li>)}
          </ul>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">FAQ — Thai documents for {c.nameEn}</h2>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <details key={i} className="rounded-lg border bg-card p-4" {...(i < 3 ? { open: true } : {})}>
                <summary className="cursor-pointer text-sm font-semibold">{f.q}</summary>
                <p className="faq-answer mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Notarisation by document type</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_DOC_LANDINGS.map((x) => (
              <a key={x.slug} href={`/en/notary/document/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.titleEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-10">
          <h2 className="mb-3 text-xl font-bold">Other destination countries</h2>
          <div className="flex flex-wrap gap-2 text-xs">
            {NOTARY_COUNTRY_LANDINGS.filter((x) => x.slug !== c.slug).map((x) => (
              <a key={x.slug} href={`/en/notary/country/${x.slug}`} className="rounded-full border bg-muted/40 px-3 py-1 text-muted-foreground hover:bg-muted">
                {x.nameEn}
              </a>
            ))}
          </div>
        </section>

        <section className="mb-6">
          <h2 className="mb-2 text-base font-bold">Sources</h2>
          <ul className="list-disc space-y-1 pl-6 text-xs text-muted-foreground">
            {c.sources.map((s: { label: string; url: string }, i: number) => (
              <li key={i}>
                <a href={s.url} target="_blank" rel="noopener nofollow" className="hover:underline">{s.label}</a>
              </li>
            ))}
          </ul>
        </section>

        <p className="mb-6 text-xs text-muted-foreground">
          Professional framework: Lawyers Council of Thailand regulation on the registration of notarial services
          attorneys, B.E. 2551 (2008) · Apostille information based on the HCCH announcement of 30 June 2026 and the Thai
          Department of Consular Affairs. Fees, timelines and country requirements change — always confirm with the
          responsible authority before acting.
        </p>

        <FaqSourceNote topic="notary" locale="en" />
      </main>
      <SiteFooter />
    </>
  );
}
